import { createPortal } from "react-dom";
import React, { useState, useEffect } from "react";
import { BotonVolver } from "@/components/BotonVolver";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { agendaPersona, useGestiones } from "@/lib/personaAgenda";
import { useOcurrencias } from "@/components/gestiones/Ocurrencias";
import { Avatar } from "@/components/personas/Avatar";
import { ColorPersona, colorLibre } from "@/components/personas/ColorPersona";
import { useData } from "@/context/DataContext";
import { formatApiErrorDetail } from "@/lib/api";

export const MiniCounter = ({ color, value, label, testid }) => (
  <div className="flex-1 text-center" data-testid={testid}>
    <p className="font-heading text-2xl font-extrabold" style={{ color }}>{value}</p>
    <p className="text-sm font-semibold text-[#4A5550] leading-tight">{label}</p>
  </div>
);

export const Contadores = ({ s, id }) => (
  <div className="flex items-start gap-2">
    <MiniCounter testid={`persona-atencion-${id}`} color="#DC2626" value={s.attention} label="Necesitan atención" />
    <div className="w-px h-10 bg-[#E5DFD5]" />
    <MiniCounter testid={`persona-proximos-${id}`} color="#D97706" value={s.upcoming} label="Próximos" />
    <div className="w-px h-10 bg-[#E5DFD5]" />
    <MiniCounter testid={`persona-aldia-${id}`} color="#16A34A" value={s.controlled} label="Al día" />
  </div>
);

export const relacionTxt = (m) => (m.es_titular ? `Titular${m.relation && !/titular/i.test(m.relation) ? ` · ${m.relation}` : ""}` : m.relation);
// "Ana (Hermana)" o solo "Ana" si no tiene relación.
export const nombreRel = (f) => (f ? `${f.name}${f.relation ? ` (${f.relation})` : ""}` : "");

const RELACIONES = ["Pareja", "Hijo/a", "Padre/Madre", "Hermano/a"];
const Relacion = ({ value, onChange }) => {
  const [otro, setOtro] = useState(!!value && !RELACIONES.includes(value));
  const sel = otro ? "otro" : value;
  return (
    <div className="mb-4">
      <label className="block text-sm font-semibold text-[#4A5550] mb-1.5">Relación <span className="font-normal text-[#9AA39D]">(opcional)</span></label>
      <select data-testid="family-relation" value={sel} onChange={(e) => { const v = e.target.value; setOtro(v === "otro"); onChange(v === "otro" ? "" : v); }}
        className="w-full min-h-[52px] px-4 rounded-xl bg-white border border-[#E5DFD5] text-base text-[#1C2421] focus:outline-none focus:border-[#3A6B56]">
        <option value="">Sin indicar</option>
        {RELACIONES.map((r) => <option key={r} value={r}>{r}</option>)}
        <option value="otro">Otro</option>
      </select>
      {otro && <input data-testid="family-relation-otro" value={value} onChange={(e) => onChange(e.target.value)} placeholder="Escribe la relación (ej. Sobrina, Abuelo…)"
        className="mt-2 w-full min-h-[52px] px-4 rounded-xl bg-white border border-[#E5DFD5] text-base text-[#1C2421] placeholder-[#9AA39D] focus:outline-none focus:border-[#3A6B56]" />}
    </div>
  );
};

// Alta/edición de un miembro de Familia.
export const PersonaSheet = ({ modal, setModal, onSaved }) => {
  const { createFamiliar, updateFamiliar, familyMembers } = useData();
  const [busy, setBusy] = useState(false);
  const color = modal.color || colorLibre(familyMembers, modal.id);
  const v = (k) => (modal[k] || "").trim();
  const save = async () => {
    if (!v("name")) return toast.error("Indica el nombre.");
    if (v("email") && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v("email"))) return toast.error("El correo electrónico no es válido.");
    setBusy(true);
    const datos = { name: v("name"), relation: v("relation"), telefono: v("telefono"), email: v("email"), direccion: v("direccion"), color };
    try {
      if (modal.mode === "create") await createFamiliar(datos);
      else await updateFamiliar(modal.id, datos);
      toast.success(modal.mode === "create" ? "Añadido a Familia" : "Datos actualizados");
      setModal(null); if (onSaved) onSaved();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo guardar."); }
    finally { setBusy(false); }
  };
  const set = (k) => (val) => setModal({ ...modal, [k]: val });
  return (
    <Sheet onClose={() => setModal(null)} testid="family-form">
      <h2 className="font-heading text-2xl font-bold text-[#1C2421] mb-5">{modal.mode === "create" ? "Añadir a Familia" : "Editar datos"}</h2>
      <Input label="Nombre" value={modal.name} onChange={set("name")} testid="family-name" placeholder="Ej. Carmen" />
      <Relacion value={modal.relation || ""} onChange={set("relation")} />
      <Input label="Teléfono (opcional)" type="tel" value={modal.telefono || ""} onChange={set("telefono")} testid="family-telefono" placeholder="+34 600 123 456" />
      <Input label="Correo electrónico (opcional)" type="email" value={modal.email || ""} onChange={set("email")} testid="family-email" placeholder="correo@ejemplo.com" />
      <Input label="Dirección (opcional)" value={modal.direccion || ""} onChange={set("direccion")} testid="family-direccion" placeholder="Calle, número, localidad" />
      <ColorPersona value={color} onChange={(c) => setModal({ ...modal, color: c })} nombre={modal.name.trim()} familyMembers={familyMembers} personaId={modal.id} />
      <button data-testid="family-save" onClick={save} disabled={busy} className="w-full min-h-[56px] rounded-xl bg-[#3A6B56] text-white font-semibold text-lg mt-4 hover:bg-[#2E5645] disabled:opacity-60">{busy ? "Guardando…" : "Guardar"}</button>
    </Sheet>
  );
};

// Eliminación con explicación de qué pasa con su información y opción de pasarla al titular.
export const BorrarPersonaSheet = ({ persona, onClose, onDone }) => {
  const { familyMembers, getMattersByMember, documents, deleteFamiliar } = useData();
  const [destino, setDestino] = useState("titular");
  const [busy, setBusy] = useState(false);
  const titular = familyMembers.find((f) => f.es_titular);
  const asuntos = getMattersByMember(persona.id);
  const docs = documents.filter((d) => d.family_member_id === persona.id);
  const nTotal = asuntos.length + docs.length;
  const borrar = async () => {
    setBusy(true);
    try { await deleteFamiliar(persona.id, destino === "titular"); toast.success(`${persona.name} eliminada de Familia`); onDone(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo eliminar."); }
    finally { setBusy(false); }
  };
  const opc = (v, t, sub) => (
    <label className="flex items-start gap-3 min-h-[52px] cursor-pointer bg-white border border-[#E5DFD5] rounded-xl p-3">
      <input type="radio" data-testid={`family-delete-destino-${v}`} checked={destino === v} onChange={() => setDestino(v)} className="w-5 h-5 mt-1 accent-[#3A6B56]" />
      <span><span className="block text-base font-semibold text-[#1C2421]">{t}</span><span className="block text-sm text-[#6E7A74]">{sub}</span></span>
    </label>
  );
  return (
    <Sheet onClose={onClose} testid="family-confirm-delete">
      <h2 className="font-heading text-xl font-bold text-[#1C2421]">¿Eliminar a {persona.name}?</h2>
      <p className="text-base text-[#4A5550] mt-2" data-testid="family-delete-explica">
        {nTotal === 0 ? "No tiene información guardada. Solo se quitará de la lista de personas."
          : `Tiene ${nTotal} ${nTotal === 1 ? "elemento" : "elementos"} guardados (asuntos y documentos). No se borrarán.`}
      </p>
      {nTotal > 0 && (
        <div className="space-y-2 mt-4">
          <p className="text-base font-semibold text-[#1C2421]">¿Qué hacemos con sus asuntos y documentos?</p>
          {titular && opc("titular", `Pasarlos a ${titular.name} (titular)`, "Seguirán apareciendo en su ficha.")}
          {opc("ninguna", "Dejarlos sin persona asignada", "Los encontrarás en sus áreas y en Todos tus documentos.")}
        </div>
      )}
      <p className="text-sm text-[#6E7A74] mt-3">Quitar a la persona no se puede deshacer.</p>
      <div className="flex gap-2.5 mt-5">
        <button data-testid="family-confirm-delete-cancel" onClick={onClose} className="flex-1 min-h-[52px] rounded-xl bg-[#F2EDE4] font-semibold text-base border border-[#E5DFD5]">Cancelar</button>
        <button data-testid="family-confirm-delete-btn" onClick={borrar} disabled={busy} className="flex-1 min-h-[52px] rounded-xl bg-[#B91C1C] text-white font-semibold text-base disabled:opacity-60">{busy ? "Eliminando…" : "Eliminar persona"}</button>
      </div>
    </Sheet>
  );
};

const Tarjeta = ({ m, gestiones, ocs, onEdit, onDelete }) => {
  const navigate = useNavigate();
  const { matters, familyMembers, reminders = [] } = useData();
  const s = agendaPersona({ p: m, matters, reminders, gestiones, ocs, familyMembers }).contadores;
  const abrir = () => navigate(`/personas/${m.id}`);
  return (
    <div data-testid={`family-profile-${m.id}`} role="button" tabIndex={0} onClick={abrir} onKeyDown={(e) => e.key === "Enter" && abrir()}
      className="cursor-pointer rounded-2xl p-4 border-2 border-[#E5DFD5] bg-white hover:border-[#3A6B56] transition-colors space-y-3">
      <div className="flex items-center gap-3">
        <Avatar persona={m} size="md" testid={`family-avatar-${m.id}`} />
        <div className="flex-1 min-w-0">
          <p className="font-heading font-bold text-lg text-[#1C2421] truncate">{m.name}</p>
          <p className="text-base text-[#4A5550]">{relacionTxt(m)}</p>
        </div>
        <button data-testid={`family-edit-${m.id}`} aria-label={`Editar a ${m.name}`} onClick={(e) => { e.stopPropagation(); onEdit(m); }} className="w-11 h-11 rounded-xl bg-[#F2EDE4] flex items-center justify-center hover:bg-[#E5DFD5]">
          <Icons.Pencil size={18} className="text-[#4A5550]" />
        </button>
        {!m.es_titular && (
          <button data-testid={`family-delete-${m.id}`} aria-label={`Eliminar a ${m.name}`} onClick={(e) => { e.stopPropagation(); onDelete(m); }} className="w-11 h-11 rounded-xl bg-[#FEF2F2] flex items-center justify-center hover:bg-[#FEE2E2]">
            <Icons.Trash2 size={18} className="text-[#B91C1C]" />
          </button>
        )}
      </div>
      <div className="pt-3 border-t border-[#E5DFD5]/70"><Contadores s={s} id={m.id} /></div>
      <button data-testid={`family-ver-${m.id}`} onClick={(e) => { e.stopPropagation(); abrir(); }}
        className="w-full min-h-[52px] rounded-xl bg-[#3A6B56] text-white font-semibold text-lg inline-flex items-center justify-center gap-2 hover:bg-[#2E5645]">
        Ver información <Icons.ChevronRight size={20} />
      </button>
    </div>
  );
};

export default function Familia() {
  const { familyMembers } = useData();
  const [modal, setModal] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const gestiones = useGestiones();
  const [ocs] = useOcurrencias();
  return (
    <div className="space-y-5" data-testid="personas-page">
      <BotonVolver porDefecto="/areas" testid="personas-volver" />
      <header className="aldia-fade-up flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl bg-[#E8F0EC] flex items-center justify-center"><Icons.Users size={26} className="text-[#3A6B56]" /></div>
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#1C2421]">Familia</h1>
          <p className="text-base text-[#4A5550]">Las personas cercanas cuyos asuntos, documentos y gastos organizas.</p>
        </div>
      </header>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="family-profiles">
        {familyMembers.map((m) => <Tarjeta key={m.id} m={m} gestiones={gestiones} ocs={ocs} onEdit={(x) => setModal({ mode: "edit", ...x })} onDelete={setConfirmDel} />)}
        <button data-testid="family-add" onClick={() => setModal({ mode: "create", name: "", relation: "" })} className="rounded-2xl p-4 border-2 border-dashed border-[#C4BDB0] bg-transparent hover:border-[#3A6B56] hover:bg-[#F6F3EE] transition-all flex items-center justify-center gap-2 min-h-[112px]">
          <Icons.UserPlus size={24} className="text-[#3A6B56]" />
          <span className="font-heading font-bold text-base text-[#3A6B56]">Añadir a Familia</span>
        </button>
      </div>
      {modal && <PersonaSheet modal={modal} setModal={setModal} />}
      {confirmDel && <BorrarPersonaSheet persona={confirmDel} onClose={() => setConfirmDel(null)} onDone={() => setConfirmDel(null)} />}
    </div>
  );
}

export const Sheet = ({ children, onClose, testid }) => {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return createPortal(
  <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" data-testid={testid}>
    <div className="absolute inset-0 bg-[#1C2421]/40 backdrop-blur-sm" onClick={onClose} />
    <div className="relative w-full sm:max-w-lg bg-[#FAF8F5] rounded-t-3xl sm:rounded-3xl p-6 pb-8 shadow-2xl aldia-fade-up max-h-[90vh] overflow-y-auto">
      <button onClick={onClose} aria-label="Cerrar" className="absolute top-4 right-4 w-11 h-11 rounded-full bg-[#F2EDE4] flex items-center justify-center">
        <Icons.X size={22} className="text-[#4A5550]" />
      </button>
      {children}
    </div>
  </div>
  , document.body);
};

export const Input = ({ label, value, onChange, placeholder, testid, type = "text" }) => (
  <div className="mb-4">
    <label className="block text-sm font-semibold text-[#4A5550] mb-1.5">{label}</label>
    <input type={type} data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
      className="w-full min-h-[52px] px-4 rounded-xl bg-white border border-[#E5DFD5] text-base text-[#1C2421] placeholder-[#9AA39D] focus:outline-none focus:ring-4 focus:ring-[#3A6B56]/20 focus:border-[#3A6B56]" />
  </div>
);
